'use client';

import { useFooter } from '@/lib/hooks/useSiteData';

const FooterNew = () => {
  const { data: footerData, loading, error } = useFooter();

  // Helper function to find a section by key
  const findSection = (key: string) => {
    if (!footerData?.data?.specifications) return null;
    const sectionsSpec = footerData.data.specifications.find((spec: any) => spec.key === 'sections');
    if (!sectionsSpec?.value) return null;
    return sectionsSpec.value.find((section: any) => section.key === key);
  };

  // Helper function to get value from a section
  const getSectionValue = (section: any, key: string) => {
    if (!section?.value) return null;
    const item = section.value.find((v: any) => v.key === key);
    return item?.value;
  };

  // Show loading state
  if (loading) {
    return (
      <footer>
        <div className="container">
          <div className="row">
            {[1, 2, 3, 4].map((col) => (
              <div key={col} className={`col-lg-${col === 1 ? '4' : col === 4 ? '2' : '3'} col-md-6`}>
                <div className="info-box">
                  <div className="placeholder-glow">
                    <h6 className="placeholder col-6 mb-3"></h6>
                    <p className="placeholder col-12 mb-2"></p>
                    <p className="placeholder col-10 mb-2"></p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="copyright-box">
            <div className="row">
              <div className="col-lg-8">
                <div className="placeholder-glow">
                  <p className="placeholder col-12 mb-2"></p>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="placeholder-glow">
                  <p className="placeholder col-8"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Get sections data
  const aboutSection = findSection('about-us');
  const newsSection = findSection('sign-up-for-e-news');
  const connectSection = findSection('connect-with-us');
  const websitesSection = findSection('our-websites');
  const extraInfoSection = findSection('extra-info');

  // Extract data from sections
  const aboutName = getSectionValue(aboutSection, 'name') || 'About Us';
  const aboutDesc = getSectionValue(aboutSection, 'description') || 'A 501c3 non-profit, our mission is to provide helpful and free resource information associated with aging, disabilities, post-military life, and overall well-being.';
  const aboutLinks = getSectionValue(aboutSection, 'links') || [];

  const newsDesc = getSectionValue(newsSection, 'description') || 'Stay informed through our e-newsletters – timely and relevant information delivered right to your inbox.';
  const newsButtonText = getSectionValue(newsSection, 'button-text') || 'Sign Up Here';
  const newsButtonLink = getSectionValue(newsSection, 'button-link') || '#';

  const connectLinks = getSectionValue(connectSection, 'links') || [];
  const socialLinks = getSectionValue(connectSection, 'social-links') || [];

  const websiteLinks = getSectionValue(websitesSection, 'links') || [];
  const helplineImage = getSectionValue(websitesSection, 'image') || '/images/logos/helpline_logo.png';

  const extraDesc = getSectionValue(extraInfoSection, 'description') || 'We strive to increase the accessibility and usability of our website and conform to W3C\'s Accessibility Guidelines (WCAG) 2.0 as well as Section 508 Guidelines.';
  const extraLinks = getSectionValue(extraInfoSection, 'links') || [];
  const copyrightText = getSectionValue(extraInfoSection, 'copy-right-text') || '© Copyright 2025 - All rights reserved.';

  return (
    <footer>
      <div className="container">
        <div className="row">
          {/* About Us Column */}
          <div className="col-lg-4 col-md-6">
            <div className="info-box">
              <h6>{aboutName}</h6>
              <div className="main-text">
                <p>{aboutDesc}</p>
              </div>
              <ul>
                {aboutLinks.map((link: any, index: number) => (
                  <li key={index}>
                    <a href={link.link || '#'}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sign Up for E-News Column */}
          <div className="col-lg-3 col-md-6">
            <div className="info-box">
              <h6>Sign Up for E-News</h6>
              <p>{newsDesc}</p>
              <a href={newsButtonLink} className="btn btn-secondary">
                <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                {newsButtonText}
              </a>
            </div>
          </div>

          {/* Connect With Us Column */}
          <div className="col-lg-3 col-md-6">
            <div className="info-box">
              <h6>Connect With Us</h6>
              <ul>
                {connectLinks.map((link: any, index: number) => (
                  <li key={index}>
                    <a href={link.links || link.link || '#'}>{link.label}</a>
                  </li>
                ))}
              </ul>
              <div className="social-links">
                <ul>
                  {socialLinks.map((social: any, index: number) => (
                    <li key={index}>
                      <a 
                        href={social.link || '#'} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <img 
                          src={social.icon} 
                          alt={`Social ${index + 1}`}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/images/icons/social_placeholder.svg';
                          }}
                        />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Our Websites Column */}
          <div className="col-lg-2 col-md-6">
            <div className="info-box">
              <h6>Our Websites</h6>
              <ul>
                {websiteLinks.map((link: any, index: number) => (
                  <li key={index}>
                    <a href={link.link || '#'}>{link.label}</a>
                  </li>
                ))}
              </ul>
              <div className="helpline-logo">
                <img 
                  src={helplineImage} 
                  alt="Helpline Logo"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/logos/helpline_logo.png';
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="copyright-box">
          <div className="row">
            <div className="col-lg-8">
              <p>{extraDesc}</p>
              <ul>
                {extraLinks.map((link: any, index: number) => (
                  <li key={index}>
                    <a href={link.link || '#'}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-4">
              <div className="copyright">
                <p>{copyrightText}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterNew;

